/**
 * Utility functions for detecting and handling maintenance mode errors
 */

import { useFirebaseRemoteConfigStore } from 'config/firebaseRemoteConfigStore';

// Flag to prevent infinite recursion when checking maintenance status
let isCheckingMaintenance = false;
let lastMaintenanceCheck = 0;
const MAINTENANCE_CHECK_COOLDOWN = 5000; // 5 seconds

/**
 * Extract error information for maintenance mode checking
 * @param errorData - The error data to extract from
 * @returns Extracted error information
 */
const extractErrorContent = (errorData: unknown): string => {
  let htmlContent = '';

  if (typeof errorData === 'string') {
    htmlContent = errorData;
  } else if (typeof errorData === 'object' && errorData !== null) {
    // Try to extract from common error object properties
    if ('bodyText' in errorData && typeof errorData.bodyText === 'string') {
      htmlContent = errorData.bodyText;
    } else if ('result' in errorData && errorData.result) {
      if (typeof errorData.result === 'string') {
        htmlContent = errorData.result;
      } else if (typeof errorData.result === 'object') {
        htmlContent = JSON.stringify(errorData.result);
      }
    } else if ('message' in errorData && typeof errorData.message === 'string') {
      htmlContent = errorData.message;
    } else {
      htmlContent = JSON.stringify(errorData);
    }
  }

  return htmlContent;
};

/**
 * Check if the error indicates maintenance mode based on status code and content
 * @param statusCode - HTTP status code
 * @param content - Error content (HTML or message)
 * @returns true if the error indicates maintenance mode, false otherwise
 */
export const checkIsMaintenanceMode = (statusCode: number, content: string): boolean => {
  if (statusCode !== 503) {
    return false;
  }

  // Parse HTML and check for "maintenance" in title
  const titleMatch = content.match(/<title[^>]*>(.*?)<\/title>/i);
  return !!(titleMatch && titleMatch[1].toLowerCase().includes('maintenance'));
};

/**
 * Check if an Apollo network error indicates maintenance mode
 * @param networkError - The network error from Apollo
 * @returns true if the error indicates maintenance mode, false otherwise
 */
export const isMaintenanceModeError = (networkError: unknown): boolean => {
  if (!networkError || typeof networkError !== 'object') {
    return false;
  }

  if (!('statusCode' in networkError)) {
    return false;
  }

  const statusCode = typeof networkError.statusCode === 'number' ? networkError.statusCode : 0;
  const content = extractErrorContent(networkError);

  return checkIsMaintenanceMode(statusCode, content);
};

/**
 * Check if an error is a network error (no response object)
 * This typically indicates CORS preflight failure or complete network failure
 * @param error - The error to check
 * @returns true if the error is a network error without a response
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  // Check if it's an Axios error without a response
  if ('isAxiosError' in error && error.isAxiosError) {
    return !('response' in error) || !error.response;
  }

  // Check for generic network errors (message contains network-related keywords)
  if ('message' in error && typeof error.message === 'string') {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') || message.includes('cors') || message.includes('failed to fetch')
    );
  }

  return false;
};

/**
 * Check if a network error is likely from our API domain (not Firebase, CDNs, etc.)
 * @param error - The error to check
 * @returns true if the error is likely from our API
 */
export const isApiNetworkError = (error: unknown): boolean => {
  if (!isNetworkError(error)) {
    return false;
  }

  // Check if it's an Axios error with config info
  const errorObj = error as { isAxiosError?: boolean; config?: { url?: string } };
  if (errorObj.isAxiosError && errorObj.config) {
    if (errorObj.config.url) {
      // Only check maintenance for requests to our API domain
      // Skip Firebase, CDNs, and other external services
      const url = errorObj.config.url.toLowerCase();
      return (
        url.includes('/graphql') ||
        url.includes('/api/') ||
        url.includes('fleetpanda') ||
        // Add other patterns for your API endpoints
        url.includes(process.env.REACT_APP_REMOTE_HOST?.toLowerCase() || '')
      );
    }
  }

  // For Apollo errors, we can't easily determine the URL, so be conservative
  // Only check maintenance for errors that are definitely network-related
  const errorWithMessage = error as { message?: string };
  if (errorWithMessage.message && typeof errorWithMessage.message === 'string') {
    const message = errorWithMessage.message.toLowerCase();
    // Only check for CORS errors (which are likely preflight failures to our API)
    return message.includes('cors');
  }

  return false;
};

/**
 * Check if an Axios error indicates maintenance mode
 * @param error - The error from Axios
 * @returns true if the error indicates maintenance mode, false otherwise
 */
export const isAxiosMaintenanceModeError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if (!('response' in error) || !error.response || typeof error.response !== 'object') {
    return false;
  }

  const response = error.response;
  if (!('status' in response)) {
    return false;
  }

  const statusCode = typeof response.status === 'number' ? response.status : 0;
  const errorData = 'data' in response ? response.data : error;
  const content = extractErrorContent(errorData);

  return checkIsMaintenanceMode(statusCode, content);
};

/**
 * Check if the system is currently in maintenance mode by fetching Firebase Remote Config
 * @param forceRefresh - Whether to force refresh the config (bypass cache)
 * @returns Promise that resolves to true if in maintenance, false otherwise
 */
export const checkMaintenanceStatus = async (forceRefresh = true): Promise<boolean> => {
  const now = Date.now();

  // Prevent infinite recursion - if we're already checking, return the current state
  if (isCheckingMaintenance) {
    return useFirebaseRemoteConfigStore.getState().config.under_maintenance.is_enabled;
  }

  // Rate limiting - don't check more than once every 5 seconds
  if (now - lastMaintenanceCheck < MAINTENANCE_CHECK_COOLDOWN) {
    return useFirebaseRemoteConfigStore.getState().config.under_maintenance.is_enabled;
  }

  try {
    isCheckingMaintenance = true;
    lastMaintenanceCheck = now;
    await useFirebaseRemoteConfigStore.getState().fetchConfig(forceRefresh);
    return useFirebaseRemoteConfigStore.getState().config.under_maintenance.is_enabled;
  } catch {
    // If config fetch fails, assume not in maintenance to avoid blocking legitimate errors
    return false;
  } finally {
    isCheckingMaintenance = false;
  }
};
